import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Lock, ExternalLink, ShieldCheck, XCircle } from "lucide-react";

export default function SharePage() {
  const { mediaId, linkId } = useParams();
  const [info, setInfo] = useState(null); // null=loading, false=error
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/share/${mediaId}/${linkId}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(false));
  }, [mediaId, linkId]);

  const unlock = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/share/${mediaId}/${linkId}/unlock`, { password: pw });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-[#0a0a0c] px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="h-6 w-1.5 rounded-full bg-[#ff2a5f]" />
          <span className="font-display text-xl font-extrabold uppercase tracking-tight text-white">MX Dance</span>
        </div>

        <div data-testid="share-card" className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-8 backdrop-blur-xl">
          {info === null ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>
          ) : info === false ? (
            <div className="space-y-3 text-center">
              <XCircle className="mx-auto h-12 w-12 text-rose-500" />
              <p className="text-white">This link is no longer available.</p>
            </div>
          ) : result ? (
            <div className="space-y-5 text-center" data-testid="share-result">
              <ShieldCheck className="mx-auto h-12 w-12 text-emerald-400" />
              <div>
                <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">{info.media_title}</div>
                <h1 className="font-display text-xl font-bold text-white">{result.label}</h1>
              </div>
              <a href={result.url} target="_blank" rel="noreferrer" data-testid="share-open">
                <Button className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
                  Open / download <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ff2a5f]/15 text-[#ff2a5f]">
                  <Lock className="h-6 w-6" />
                </div>
                <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">{info.media_title}</div>
                <h1 className="font-display text-xl font-bold text-white">{info.label}</h1>
              </div>
              {info.has_password ? (
                <>
                  <p className="text-center text-sm text-zinc-400">Enter the access password to open this link.</p>
                  <Input
                    data-testid="share-password-input"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Access password"
                    onKeyDown={(e) => e.key === "Enter" && unlock()}
                    className="border-zinc-700 bg-zinc-950 text-white"
                  />
                </>
              ) : (
                <p className="text-center text-sm text-zinc-400">Click below to open this link.</p>
              )}
              {err && <p className="text-center text-sm text-rose-400">{err}</p>}
              <Button data-testid="share-unlock" onClick={unlock} disabled={busy} className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
